const makeField = (id, label, value, role = 'custom') => ({ id, label, value, role });

const genericPreset = (language) => [
  makeField('health', language === 'fr' ? 'Santé / endurance' : 'Health / endurance', 10, 'health'),
  makeField('defense', language === 'fr' ? 'Défense' : 'Defense', 10, 'defense'),
  makeField('initiative', 'Initiative', 0, 'initiative'),
  makeField('movement', language === 'fr' ? 'Déplacement' : 'Movement', '', 'movement'),
];

const dndPreset = () => [
  makeField('health', 'PV', 10, 'health'),
  makeField('defense', 'CA', 10, 'defense'),
  makeField('initiative', 'Initiative', 0, 'initiative'),
  makeField('movement', 'Vitesse', '30 ft', 'movement'),
  makeField('rating', 'FP', '', 'rating'),
  makeField('reward', 'XP', 0, 'reward'),
  makeField('strength', 'FOR', 10),
  makeField('dexterity', 'DEX', 10),
  makeField('constitution', 'CON', 10),
  makeField('intelligence', 'INT', 10),
  makeField('wisdom', 'SAG', 10),
  makeField('charisma', 'CHA', 10),
];

export const createCreatureStatFields = (systemSlug, language = 'fr') => (
  systemSlug === 'dnd5e' ? dndPreset() : genericPreset(language)
);

const legacyFields = (combat) => {
  if (!combat || typeof combat !== 'object') return [];
  const legacyKeys = ['ac', 'max_hp', 'initiative_modifier', 'speed', 'cr', 'xp', 'str_score', 'dex_score', 'con_score', 'int_score', 'wis_score', 'cha_score'];
  if (!legacyKeys.some(key => combat[key] !== undefined)) return [];

  return [
    makeField('health', 'PV', combat.max_hp ?? 10, 'health'),
    makeField('defense', 'CA', combat.ac ?? 10, 'defense'),
    makeField('initiative', 'Initiative', combat.initiative_modifier ?? 0, 'initiative'),
    makeField('movement', 'Vitesse', combat.speed ?? '30 ft', 'movement'),
    makeField('rating', 'FP', combat.cr ?? '', 'rating'),
    makeField('reward', 'XP', combat.xp ?? 0, 'reward'),
    makeField('strength', 'FOR', combat.str_score ?? 10),
    makeField('dexterity', 'DEX', combat.dex_score ?? 10),
    makeField('constitution', 'CON', combat.con_score ?? 10),
    makeField('intelligence', 'INT', combat.int_score ?? 10),
    makeField('wisdom', 'SAG', combat.wis_score ?? 10),
    makeField('charisma', 'CHA', combat.cha_score ?? 10),
  ];
};

export const getCreatureStatFields = (entityData, fallbackFields = []) => {
  const combat = entityData?.combat || {};
  if (Array.isArray(combat.fields)) {
    return combat.fields.map((field, index) => ({
      id: field.id || `stat-${index}`,
      label: field.label || '',
      value: field.value ?? '',
      role: field.role || 'custom',
    }));
  }
  const migrated = legacyFields(combat);
  return migrated.length ? migrated : fallbackFields;
};

export const buildCreatureEntityData = (fields, previousEntityData = {}) => ({
  ...previousEntityData,
  combat: {
    schema_version: 2,
    fields: fields.map(field => ({
      id: field.id,
      label: field.label,
      value: field.value,
      role: field.role || 'custom',
    })),
  },
});

export const getCreatureStatByRole = (fields, role) => fields.find(field => field.role === role);

const numberValue = (field, fallback) => {
  if (field?.value === '' || field?.value === null || field?.value === undefined) return fallback;
  const parsed = Number(field?.value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export const buildCombatantFromCreature = (creature, language = 'fr') => {
  const fields = getCreatureStatFields(creature.entity_data);
  const health = getCreatureStatByRole(fields, 'health');
  const defense = getCreatureStatByRole(fields, 'defense');
  const initiative = getCreatureStatByRole(fields, 'initiative');
  const movement = getCreatureStatByRole(fields, 'movement');
  const rating = getCreatureStatByRole(fields, 'rating');
  const reward = getCreatureStatByRole(fields, 'reward');
  const maxHealth = Math.max(1, numberValue(health, 10));

  const byId = Object.fromEntries(fields.map(field => [field.id, field]));
  return {
    name: creature.title,
    combatant_type: 'monster',
    initiative: 0,
    initiative_modifier: numberValue(initiative, 0),
    ac: Math.max(0, numberValue(defense, 10)),
    max_hp: maxHealth,
    current_hp: maxHealth,
    temp_hp: 0,
    speed: movement?.value ? String(movement.value) : '',
    cr: rating?.value !== undefined && rating?.value !== '' ? String(rating.value) : null,
    xp: Math.max(0, numberValue(reward, 0)),
    str_score: numberValue(byId.strength, 10),
    dex_score: numberValue(byId.dexterity, 10),
    con_score: numberValue(byId.constitution, 10),
    int_score: numberValue(byId.intelligence, 10),
    wis_score: numberValue(byId.wisdom, 10),
    cha_score: numberValue(byId.charisma, 10),
    notes: creature.summary || '',
    linked_lore_id: creature.id,
    system_stats: fields,
    tracker_labels: {
      health: health?.label || (language === 'fr' ? 'Santé' : 'Health'),
      defense: defense?.label || (language === 'fr' ? 'Défense' : 'Defense'),
      initiative: initiative?.label || 'Initiative',
      movement: movement?.label || (language === 'fr' ? 'Déplacement' : 'Movement'),
      rating: rating?.label || (language === 'fr' ? 'Niveau' : 'Rating'),
      reward: reward?.label || (language === 'fr' ? 'Récompense' : 'Reward'),
    },
  };
};
